import { useEffect, useMemo, useRef } from "react";

import { onboarding } from "byzantine";

import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import {
  guideActions,
  useOnboardingGuideStorage,
} from "../../../guides/storage";

import type { OnboardingPrompt } from "byzantine/src/dbbl/businessLogic/modules/onboarding";

const { OnboardingPromptStatus } = onboarding;

interface UseGuideCompletionAnimationOptions {
  translatedPrompts: OnboardingPrompt[];
  rawPrompts: OnboardingPrompt[];
  fetchOnboardingPrompts: () => void;
  dismissingUuid: string | null;
  dismissPrompt: (uuid: string) => void;
}

/**
 * Returns the prompts to show, and slides one out when it completes.
 *
 * When a guide action completes elsewhere it sets `forceReset`. We then
 * refetch, find which on-screen prompt became "completed", and animate it out
 * instead of letting it vanish.
 */
export const useGuideCompletionAnimation = ({
  translatedPrompts,
  rawPrompts,
  fetchOnboardingPrompts,
  dismissingUuid,
  dismissPrompt,
}: UseGuideCompletionAnimationOptions): OnboardingPrompt[] => {
  // In-progress prompts on screen when forceReset fired; re-checked after the
  // refetch to see which one completed.
  const awaitingCompletionRef = useRef<string[]>([]);
  // Prompts whose slide-out we already started (so we don't start it twice).
  const animatedUuidsRef = useRef<Set<string>>(new Set());

  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id?.toString() || "";

  const [guideState, setGuideState] = useOnboardingGuideStorage(userId);
  // The completed prompt to animate out, if any.
  const pendingCompletionUuid = useMemo(
    () =>
      awaitingCompletionRef.current.find((uuid) =>
        rawPrompts.some(
          (p) =>
            p.uuid === uuid && p.status === OnboardingPromptStatus.completed
        )
      ) ?? null,
    [rawPrompts]
  );

  // A completed prompt is normally filtered out; keep the animating one visible.
  const keepVisibleUuid = dismissingUuid ?? pendingCompletionUuid;

  const visiblePrompts = useMemo(
    () =>
      translatedPrompts.filter(
        (prompt) =>
          !guideActions.isPromptDismissed(guideState, prompt.uuid) &&
          (prompt.status !== OnboardingPromptStatus.completed ||
            prompt.uuid === keepVisibleUuid)
      ),
    [translatedPrompts, guideState, keepVisibleUuid]
  );

  // Mirror visiblePrompts into a ref so the forceReset effect can read it
  // without depending on it (which would re-run it on every prompt change).
  const visiblePromptsRef = useRef<OnboardingPrompt[]>(visiblePrompts);
  visiblePromptsRef.current = visiblePrompts;

  // On forceReset: remember the visible in-progress prompts, refetch, clear flag.
  useEffect(() => {
    if (!guideState.forceReset) {
      return;
    }
    awaitingCompletionRef.current = visiblePromptsRef.current
      .filter((p) => p.status === OnboardingPromptStatus.inProgress)
      .map((p) => p.uuid);
    animatedUuidsRef.current = new Set();
    fetchOnboardingPrompts();
    setGuideState(guideActions.clearForceReset);
  }, [guideState.forceReset, fetchOnboardingPrompts, setGuideState]);

  // Once the refetch marks one completed, slide it out (top card only).
  useEffect(() => {
    if (
      pendingCompletionUuid &&
      !dismissingUuid &&
      !animatedUuidsRef.current.has(pendingCompletionUuid)
    ) {
      animatedUuidsRef.current.add(pendingCompletionUuid);
      awaitingCompletionRef.current = [];
      dismissPrompt(pendingCompletionUuid);
    }
  }, [pendingCompletionUuid, dismissingUuid, dismissPrompt]);

  return visiblePrompts;
};
